import type { PropsWithoutRef, Ref } from 'react';
import { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useI18n, useTheme } from '../../hooks';
import type { RefElement, WithAttributes } from '../../types';
import { createUID } from '../../utils';
import Progress from '../Progress';

import { StyledLocationError, StyledLocationView, StyledMap } from './LocationView.styles';
import type { LatLng, Pin } from './Location.types';
import MapsContext from './MapsContext';
import { isLatLngObject, loadMapsAPI, toGoogleLatLng } from './utils';
import type { LocationAPI } from './LocationAPI.types';

export type LocationViewProps = WithAttributes<
  'div',
  {
    /**
     * Array of location objects.
     * @default []
     */
    pins?: Pin[];
    /**
     * Set to true to always center the map when the selected location changes. Toggling this from false to true will re-center map on the last pinned location.
     * @default false
     */
    centerMapOnChange?: boolean;
    /**
     * Height of the map container.
     * @default '25rem'
     */
    height?: string;
    /**
     * How much the map should zoom.
     * @default 13
     */
    zoomLevel?: number;
    /** Callback fired when user clicks on a location on the map. This function will be passed the coordinates of the location that was clicked. */
    onClick?: (
      locationData: LatLng & {
        name?: string;
        address?: string;
      }
    ) => void;
    /**
     * Renders loading indicator when true. Informs the user location resolution is in progress.
     */
    loading?: boolean;
    /**
     * Disables dragging and clicking when true.
     * @default false
     */
    disabled?: boolean;
    /**
     * Indicates if route between pins should be rendered.
     * @default false
     */
    drawRoute?: boolean;

    /** Callback fired when an error occurs. This function gets called with one argument of type Error. */
    onError?: (error: Error) => void;
    /** Function that returns the current autocomplete session token. */
    getSessionToken?: () => google.maps.places.AutocompleteSessionToken | undefined;
  }
>;

enum LoadStatus {
  Init = 'init',
  Ready = 'ready',
  Error = 'error'
}

const hasPlaceId = (obj: unknown): obj is google.maps.IconMouseEvent & { placeId: string } => {
  return (
    typeof obj === 'object' &&
    !!obj &&
    'placeId' in obj &&
    !!obj.placeId &&
    typeof obj.placeId === 'string'
  );
};

/* cSpell:ignore 'ABCDEFGHIJKLMNOPQRSTUVWYZ' */
const markerLabels = 'ABCDEFGHIJKLMNOPQRSTUVWYZ';

const LocationView = forwardRef<RefElement<LocationViewProps>, PropsWithoutRef<LocationViewProps>>(
  function LocationView(props, ref) {
    const {
      pins = [],
      zoomLevel = 13,
      height = '25rem',
      centerMapOnChange = false,
      onClick,
      onError,
      disabled = false,
      loading = false,
      drawRoute = false,
      getSessionToken,
      ...restProps
    } = props;
    const t = useI18n();
    const { name, ...providerOpts } = useContext(MapsContext);
    const [status, setStatus] = useState<LoadStatus>(LoadStatus.Init);
    const [eMessage, setEMessage] = useState('');
    const [selectedPinIndex, setSelectedPinIndex] = useState<number | undefined>(undefined);
    const [mapAPIObj, setmapAPIObj] = useState<LocationAPI>();
    const mapElemRef = useRef<HTMLDivElement>();
    const map = useRef<google.maps.Map>();
    const markers = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
    const ds = useRef<google.maps.DirectionsService>();
    const dr = useRef<google.maps.DirectionsRenderer>();
    const infoWindowContent = useRef<HTMLDivElement>(document.createElement('div'));
    const theme = useTheme();

    const onMapClick = (e: google.maps.MapMouseEvent) => {
      if (!isLatLngObject(e.latLng) || !mapAPIObj) return;

      if (hasPlaceId(e)) {
        mapAPIObj.getPlaceById(e.placeId, getSessionToken?.()).then(placeInfo => {
          onClick?.({
            ...placeInfo,
            latitude: e.latLng!.lat(),
            longitude: e.latLng!.lng()
          });
        });
      } else {
        mapAPIObj
          .getAddress({
            latitude: e.latLng.lat(),
            longitude: e.latLng.lng()
          })
          .then(onClick)
          .catch(err => onError?.(err));
      }
    };

    const centerMap = useCallback((coords: LatLng[]) => {
      if (!map.current || coords.length === 0) return;

      if (coords.length === 1) {
        map.current.setCenter(toGoogleLatLng(coords[0]));
        map.current.setZoom(zoomLevel);
      } else {
        const bounds = new google.maps.LatLngBounds();
        coords.forEach(({ latitude, longitude }) => {
          bounds.extend(toGoogleLatLng({ latitude, longitude }));
        });
        map.current.fitBounds(bounds);
      }
    }, []);

    const initialize = () => {
      if (mapElemRef.current) {
        const position = toGoogleLatLng({ latitude: 0, longitude: 0 });
        map.current = new google.maps.Map(mapElemRef.current, {
          mapId: createUID(),
          center: position,
          zoom: 2,
          draggable: !disabled,
          draggableCursor: 'auto',
          draggingCursor: 'move'
        });
        ds.current = new google.maps.DirectionsService();
        dr.current = new google.maps.DirectionsRenderer({ suppressMarkers: true });
        centerMap(pins);
      }
    };

    useEffect(() => {
      if (!onClick || !map.current) return;

      const l = map.current.addListener('click', onMapClick);
      return () => google.maps.event.removeListener(l);
    }, [onClick, map.current, getSessionToken]);

    useEffect(() => {
      let mounted = true;
      loadMapsAPI(name, providerOpts)
        .then(APIObj => {
          setmapAPIObj(APIObj);

          if (mounted) {
            initialize();
            setStatus(LoadStatus.Ready);
          }
        })
        .catch((error: Error) => {
          setStatus(LoadStatus.Error);
          setEMessage(error.message);
          onError?.(error);
        });

      return () => {
        mounted = false;
        if (map.current) google.maps.event.clearInstanceListeners(map.current);
      };
    }, []);

    useEffect(() => {
      if (
        status !== LoadStatus.Ready ||
        !map.current ||
        !dr.current ||
        !ds.current ||
        // FIXME: Workaround for marker library not being imported
        !google.maps.marker
      )
        return;

      if (drawRoute && pins.length > 1) {
        dr.current.setMap(map.current);
        const request: google.maps.DirectionsRequest = {
          origin: toGoogleLatLng({ latitude: pins[0].latitude, longitude: pins[0].longitude }),
          destination: toGoogleLatLng({
            latitude: pins[pins.length - 1].latitude,
            longitude: pins[pins.length - 1].longitude
          }),
          waypoints: pins.slice(1, pins.length - 1).map(l => {
            return {
              stopover: true,
              location: toGoogleLatLng({
                latitude: l.latitude,
                longitude: l.longitude
              })
            };
          }),
          travelMode: google.maps.TravelMode.DRIVING
        };

        ds.current.route(request)?.then(dr.current.setDirections.bind(dr.current), onError);
      }

      const infoWindow = new google.maps.InfoWindow();

      try {
        markers.current = pins.map(({ latitude, longitude, title, content, selected }, index) => {
          const gCoords = toGoogleLatLng({ latitude, longitude });

          const contentConfig: google.maps.marker.PinElementOptions = {
            glyphColor: theme.base.colors.white
          };
          const label = markerLabels[index % markerLabels.length];
          if (pins.length > 1) {
            const glyphWrapper = document.createElement('span');
            glyphWrapper.style.fontSize = '0.9rem';
            glyphWrapper.style.paddingTop = '0.1rem';
            glyphWrapper.innerHTML = label;
            contentConfig.glyph = glyphWrapper;
          }
          if (selected) {
            contentConfig.background = theme.base.colors.red.dark;
            contentConfig.borderColor = theme.base.colors.red.dark;
            contentConfig.scale = 1.15;
          }
          const pin = new google.maps.marker.PinElement(contentConfig);
          const marker = new google.maps.marker.AdvancedMarkerElement({
            map: map.current,
            position: gCoords,
            title,
            content: pin.element
          });
          if (content) {
            marker.addListener('click', () => {
              setSelectedPinIndex(index);
              infoWindow.setOptions({
                ariaLabel: `${t('location_info')} - ${title ?? label}`,
                headerContent: title ?? label
              });
              infoWindow.setContent(infoWindowContent.current);
              infoWindow.open({
                anchor: marker,
                map: map.current
              });
            });
          }
          return marker;
        });
      } catch {
        markers.current = [];
      }

      if (centerMapOnChange) {
        centerMap(pins);
      }

      return () => {
        markers.current.forEach(m => {
          m.map = null;
        });
        dr.current?.setMap(null);
      };
    }, [
      JSON.stringify(pins, ['title', 'selected', 'latitude', 'longitude']),
      status,
      zoomLevel,
      disabled,
      drawRoute,
      centerMapOnChange,
      onError
    ]);

    return (
      <StyledLocationView $height={height} ref={ref}>
        <StyledMap
          ref={mapElemRef as Ref<HTMLDivElement>}
          disabled={disabled}
          status={status}
          {...restProps}
        />
        {(status === LoadStatus.Init || loading) && <Progress placement='local' />}
        {status === LoadStatus.Error && eMessage && (
          <StyledLocationError aria-label={eMessage} variant='info'>
            {eMessage}
          </StyledLocationError>
        )}
        {selectedPinIndex !== undefined && !!pins[selectedPinIndex].content && (
          <>{createPortal(pins[selectedPinIndex].content, infoWindowContent.current)}</>
        )}
      </StyledLocationView>
    );
  }
);

export default LocationView;
